import {
  BarChartOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { Stack } from '@sureco/design-system'
import {
  Button,
  Card,
  Dropdown,
  Modal,
  Progress,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from '@surecompanies/core_components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import epsClient from 'enrollment-platform/api/eps.client'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { browserHistory } from 'react-router'
import { canUseCompanyIgnore } from '../../companyIgnorePermissions'
import CompanyIgnore from '../../components/CompanyIgnore'
import {
  TEMPLATE_TYPE_LABELS,
  TEMPLATE_TYPES,
  TemplateType,
} from '../../models/templateType'
import {
  CENSUS_ONBOARDING,
  CENSUS_TEMPLATE_CREATE,
  CENSUS_TEMPLATE_EDIT,
  CENSUS_TEMPLATE_VIEW,
  ELIGIBLE_RULES_CREATE,
  ELIGIBLE_RULES_EDIT,
  ELIGIBLE_RULES_VIEW,
} from '../../paths'
import styles from './CustomerDetail.module.scss'

interface HrisMappingTemplate {
  id: string
  clientPublicKey: string
  type: TemplateType
  name: string
  description?: string
  versionNumber: number
  mappingRules: {
    schema_version: string
    mapping_rules: Array<{
      target: string
      condition: string
      [key: string]: unknown
    }>
  }
  status: string
  changeSummary?: string
  createdBy: string
  createdAt: string
  updatedAt: string
}

interface EligibleRuleTemplate {
  id: string
  clientPublicKey: string
  templateName: string
  description?: string
  versionNumber: number
  ruleDefinition: Record<string, unknown>
  status: string
  createdBy: string
  createdByName?: string
  createdAt: string
  updatedAt: string
}

/** Match API active template regardless of casing / stray whitespace */
function isActiveHrisTemplateStatus(status: string | undefined): boolean {
  return (status ?? '').trim().toUpperCase() === 'ACTIVE'
}

const statusConfig: Record<
  string,
  { color: string; label: string; tooltip: string }
> = {
  DRAFT: {
    color: '#4a5568',
    label: 'DRAFT',
    tooltip:
      'This template is being configured and has not been finalized. You can edit its mapping rules freely.',
  },
  PUBLISHED: {
    color: '#003146',
    label: 'PUBLISHED',
    tooltip:
      'This template has been finalized and is ready for activation. Mapping rules are locked.',
  },
  ACTIVE: {
    color: 'green',
    label: 'ACTIVE',
    tooltip: 'This template is currently in use for processing census data.',
  },
  ARCHIVED: {
    color: 'default',
    label: 'ARCHIVED',
    tooltip:
      'This template was once active but has since been replaced or deactivated. It is retained for historical reference only.',
  },
}

function useEligibleRuleHistory(clientPublicKey: string) {
  return useQuery<EligibleRuleTemplate[]>({
    queryKey: ['eligible-rule-templates', clientPublicKey],
    queryFn: async () => {
      const result = await epsClient.get(
        `/api/v1/eligible-rule-templates/clients/${clientPublicKey}`
      )
      return result.data
    },
    enabled: !!clientPublicKey,
  })
}

function getTemplateColumns(
  customerId: string,
  onActivate: (id: string) => void,
  onArchive: (id: string) => void
) {
  return [
    {
      title: 'Version',
      dataIndex: 'versionNumber',
      key: 'version',
      render: (value: number) => (
        <Typography.Text code>v{value}</Typography.Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (value: string) => value || '—',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (value: string, record: HrisMappingTemplate) =>
        value || record.description || '—',
    },
    {
      title: 'Fields Mapped',
      dataIndex: 'mappingRules',
      key: 'fieldsMapped',
      render: (value: HrisMappingTemplate['mappingRules']) =>
        value?.mapping_rules?.length ?? 0,
    },
    {
      title: 'Created By',
      dataIndex: 'createdByName',
      key: 'createdByName',
      render: (value: string) => (
        <span>
          <span style={{ marginRight: 6, color: '#677786' }}>👤</span>
          {value}
        </span>
      ),
    },
    {
      title: 'Published Date',
      dataIndex: 'createdAt',
      key: 'publishedDate',
      render: (value: string) => (
        <span>
          <span style={{ marginRight: 6, color: '#677786' }}>🕐</span>
          {value ? dayjs(value).format('M/D/YYYY h:mm A') : '—'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const config = statusConfig[status] ?? statusConfig.ARCHIVED
        return (
          <Tooltip title={config.tooltip}>
            <Tag
              color={config.color}
              style={{
                borderRadius: 12,
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {config.label}
            </Tag>
          </Tooltip>
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: HrisMappingTemplate) => {
        const items = [
          ...(record.status === 'DRAFT'
            ? [
                {
                  key: 'edit',
                  icon: <EditOutlined />,
                  label: 'Edit',
                  onClick: () =>
                    browserHistory.push(
                      `/${CENSUS_ONBOARDING}/${customerId}/${CENSUS_TEMPLATE_EDIT}/${record.id}`
                    ),
                },
              ]
            : []),
          ...(record.status === 'PUBLISHED' ||
          record.status === 'ACTIVE' ||
          record.status === 'ARCHIVED'
            ? [
                {
                  key: 'view',
                  icon: <EyeOutlined />,
                  label: 'View',
                  onClick: () =>
                    browserHistory.push(
                      `/${CENSUS_ONBOARDING}/${customerId}/${CENSUS_TEMPLATE_VIEW}/${record.id}`
                    ),
                },
              ]
            : []),
          ...(record.status === 'PUBLISHED'
            ? [
                {
                  key: 'activate',
                  icon: <CheckCircleOutlined />,
                  label: 'Activate',
                  onClick: () => onActivate(record.id),
                },
                {
                  key: 'archive',
                  icon: <DeleteOutlined />,
                  label: 'Archive',
                  danger: true,
                  onClick: () => onArchive(record.id),
                },
              ]
            : []),
        ]
        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]
}

function getEligibleRuleColumns(
  customerId: string,
  onActivate: (record: EligibleRuleTemplate) => void
) {
  return [
    {
      title: 'Version',
      dataIndex: 'versionNumber',
      key: 'version',
      render: (value: number) => (
        <Typography.Text code>v{value}</Typography.Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'templateName',
      key: 'templateName',
      render: (value: string) => value || '—',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (value: string) => value || '—',
    },
    {
      title: 'Created By',
      dataIndex: 'createdByName',
      key: 'createdByName',
      render: (value: string) => (
        <span>
          <span style={{ marginRight: 6, color: '#677786' }}>👤</span>
          {value}
        </span>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => (
        <span>
          <span style={{ marginRight: 6, color: '#677786' }}>🕐</span>
          {value ? dayjs(value).format('M/D/YYYY h:mm A') : '—'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const config = statusConfig[status] ?? statusConfig.ARCHIVED
        return (
          <Tooltip title={config.tooltip}>
            <Tag
              color={config.color}
              style={{
                borderRadius: 12,
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {config.label}
            </Tag>
          </Tooltip>
        )
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: EligibleRuleTemplate) => {
        const items = [
          ...(record.status === 'DRAFT'
            ? [
                {
                  key: 'edit',
                  icon: <EditOutlined />,
                  label: 'Edit',
                  onClick: () =>
                    browserHistory.push(
                      `/${CENSUS_ONBOARDING}/${customerId}/${ELIGIBLE_RULES_EDIT}/${record.id}`
                    ),
                },
              ]
            : []),
          ...(record.status === 'PUBLISHED' ||
          record.status === 'ACTIVE' ||
          record.status === 'ARCHIVED'
            ? [
                {
                  key: 'view',
                  icon: <EyeOutlined />,
                  label: 'View',
                  onClick: () =>
                    browserHistory.push(
                      `/${CENSUS_ONBOARDING}/${customerId}/${ELIGIBLE_RULES_VIEW}/${record.id}`
                    ),
                },
              ]
            : []),
          ...(record.status === 'PUBLISHED'
            ? [
                {
                  key: 'activate',
                  icon: <CheckCircleOutlined />,
                  label: 'Activate',
                  onClick: () => onActivate(record),
                },
              ]
            : []),
        ]
        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        )
      },
    },
  ]
}

interface CustomerDetailProps {
  params?: { id: string }
}

function useCustomerDetail(id: string) {
  return useQuery({
    queryKey: ['getCustomer', id],
    queryFn: async () => {
      const result = await epsClient.get(`/customers/${id}`)
      return result.data
    },
    enabled: !!id,
  })
}

function useTemplateHistory(clientPublicKey: string) {
  return useQuery<HrisMappingTemplate[]>({
    queryKey: ['hris-mapping-templates', clientPublicKey],
    queryFn: async () => {
      const result = await epsClient.get(
        `/api/v1/hris-mapping-templates/client/${clientPublicKey}`
      )
      return result.data
    },
    enabled: !!clientPublicKey,
  })
}

export default function CustomerDetail({ params }: CustomerDetailProps) {
  const customerId = params?.id ?? ''
  const queryClient = useQueryClient()
  const role = useSelector(authSelectors.getRole)
  const showCompanyIgnore = canUseCompanyIgnore(role as string)
  const { data: customer, isPending } = useCustomerDetail(customerId)
  const { data: templateHistory = [], isPending: isTemplatesPending } =
    useTemplateHistory(customerId)
  const {
    data: rawEligibleRulesHistory = [],
    isPending: isEligibleRulesPending,
  } = useEligibleRuleHistory(customerId)

  const eligibleRulesHistory = useMemo(() => {
    const chronological = [...rawEligibleRulesHistory].sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )
    return chronological
      .map((item, index) => ({
        ...item,
        versionNumber: item.versionNumber ?? index + 1,
      }))
      .reverse()
  }, [rawEligibleRulesHistory])

  const templatesByType = useMemo(() => {
    const map: Record<TemplateType, HrisMappingTemplate[]> = {
      AMA: [],
      API_INTEGRATION: [],
    }
    for (const tpl of templateHistory) {
      const t: TemplateType = tpl.type ?? 'AMA'
      if (map[t]) {
        map[t].push(tpl)
      }
    }
    return map
  }, [templateHistory])

  const activeByType = useMemo(() => {
    const map: Record<TemplateType, HrisMappingTemplate | null> = {
      AMA: null,
      API_INTEGRATION: null,
    }
    for (const type of TEMPLATE_TYPES) {
      map[type] =
        templatesByType[type].find((t) =>
          isActiveHrisTemplateStatus(t.status)
        ) ?? null
    }
    return map
  }, [templatesByType])

  const customerName = customer?.name ?? '—'
  const customerStatus = customer?.status?.toLowerCase() ?? 'mapping'

  const activateEligibleRule = useMutation({
    mutationFn: (templateId: string) =>
      epsClient.patch(`/api/v1/eligible-rule-templates/${templateId}/status`, {
        action: 'activate',
      }),
    onSuccess: () => {
      message.success('Eligible rule activated successfully')
      queryClient.invalidateQueries({
        queryKey: ['eligible-rule-templates', customerId],
      })
    },
    onError: (err: any) => {
      const detail =
        err?.response?.data?.message ?? err?.message ?? 'Unknown error'
      message.error(`Failed to activate eligible rule: ${detail}`)
    },
  })

  const handleActivateEligibleRule = useCallback(
    (record: EligibleRuleTemplate) => {
      Modal.confirm({
        title: 'Activate Eligible Rule',
        content: `Are you sure you want to activate "${record.templateName}" (v${record.versionNumber})? This will replace the currently active eligible rule.`,
        okText: 'Activate',
        okType: 'primary',
        onOk: () => activateEligibleRule.mutateAsync(record.id),
      })
    },
    [activateEligibleRule]
  )
  const hasAnyActiveTemplate = useMemo(
    () => TEMPLATE_TYPES.some((type) => activeByType[type] !== null),
    [activeByType]
  )

  const handleActivate = useCallback(
    (templateId: string) => {
      Modal.confirm({
        title: 'Activate Template',
        content:
          'This will activate this template and deactivate the current active version. Are you sure?',
        okText: 'Activate',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await epsClient.post(
              `/api/v1/hris-mapping-templates/${templateId}/activate`
            )
            message.success('Template activated successfully')
            queryClient.invalidateQueries({
              queryKey: ['hris-mapping-templates', customerId],
            })
          } catch {
            message.error('Failed to activate template')
          }
        },
      })
    },
    [customerId, queryClient]
  )

  const handleArchive = useCallback(
    (templateId: string) => {
      Modal.confirm({
        title: 'Archive Template',
        content:
          'This will archive this template. It will no longer be available for activation. Are you sure?',
        okText: 'Archive',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await epsClient.patch(
              `/api/v1/hris-mapping-templates/${templateId}/archive`
            )
            message.success('Template archived successfully')
            queryClient.invalidateQueries({
              queryKey: ['hris-mapping-templates', customerId],
            })
          } catch {
            message.error('Failed to archive template')
          }
        },
      })
    },
    [customerId, queryClient]
  )

  const handleCreateTemplate = useCallback(
    (type: TemplateType) => {
      const templatesForType = templatesByType[type]
      const activeForType = activeByType[type]
      if (templatesForType.length > 0) {
        if (!activeForType) {
          message.warning(
            `Activate a ${TEMPLATE_TYPE_LABELS[type]} field mapping template before creating a new version.`
          )
          return
        }
        browserHistory.push(
          `/${CENSUS_ONBOARDING}/${customerId}/${CENSUS_TEMPLATE_CREATE}?type=${type}&from=${activeForType.id}`
        )
        return
      }
      browserHistory.push(
        `/${CENSUS_ONBOARDING}/${customerId}/${CENSUS_TEMPLATE_CREATE}?type=${type}`
      )
    },
    [customerId, templatesByType, activeByType]
  )

  const handleCreateEligibleRules = useCallback(() => {
    browserHistory.push(
      `/${CENSUS_ONBOARDING}/${customerId}/${ELIGIBLE_RULES_CREATE}`
    )
  }, [customerId])

  const templateColumns = useMemo(
    () => getTemplateColumns(customerId, handleActivate, handleArchive),
    [customerId, handleActivate, handleArchive]
  )

  const eligibleRuleColumns = useMemo(
    () => getEligibleRuleColumns(customerId, handleActivateEligibleRule),
    [customerId, handleActivateEligibleRule]
  )

  const hasCompletedTemplate = hasAnyActiveTemplate
  const hasCompletedEligibleRule = eligibleRulesHistory.some(
    (t) => t.status === 'ACTIVE' || t.status === 'PUBLISHED'
  )

  const progress = useMemo(() => {
    const totalSteps = 2
    let completedSteps = 0
    if (hasCompletedTemplate) completedSteps += 1
    if (hasCompletedEligibleRule) completedSteps += 1
    return { completedSteps, totalSteps }
  }, [hasCompletedTemplate, hasCompletedEligibleRule])

  if (isPending) {
    return (
      <Page title="">
        <Stack align="center" justify="center" style={{ height: '100%' }}>
          <Spin spinning />
        </Stack>
      </Page>
    )
  }

  return (
    <Page
      title={
        <div className={styles.companyTitle}>
          <span>{customerName}</span>
          <Tag
            color="#003146"
            style={{ borderRadius: 12, fontWeight: 600, fontSize: 12 }}
          >
            {customerStatus}
          </Tag>
        </div>
      }
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Home', path: '/' },
            { title: 'Census Onboarding', path: '/census-onboarding' },
            { title: customerName },
          ]}
        />
      }
    >
      <Card className={styles.progressCard}>
        <div className={styles.progressHeader}>
          <span className={styles.progressTitle}>Onboarding Progress</span>
          <span className={styles.progressCount}>
            {progress.completedSteps} of {progress.totalSteps} steps completed
          </span>
        </div>
        <Progress
          percent={(progress.completedSteps / progress.totalSteps) * 100}
          showInfo={false}
          strokeColor="#003146"
        />
      </Card>

      <div className={styles.stepsGrid}>
        {isTemplatesPending ? (
          <Spin spinning />
        ) : (
          TEMPLATE_TYPES.map((type) =>
            templatesByType[type].length === 0 ? (
              <div className={styles.stepCard} key={`empty-${type}`}>
                <div className={styles.stepInfo}>
                  <div className={styles.stepIcon}>
                    <FileTextOutlined />
                  </div>
                  <div>
                    <div className={styles.stepTitle}>
                      Configure Field Mapping — {TEMPLATE_TYPE_LABELS[type]}
                    </div>
                    <p className={styles.stepDescription}>
                      Map census headers to CMv2 fields with transformations
                    </p>
                  </div>
                </div>
                <Button
                  type="primary"
                  size="large"
                  className={styles.stepButton}
                  onClick={() => handleCreateTemplate(type)}
                >
                  <PlusOutlined /> Create new template
                </Button>
              </div>
            ) : null
          )
        )}

        {isEligibleRulesPending ? (
          <Spin spinning />
        ) : (
          eligibleRulesHistory.length === 0 && (
            <div className={styles.stepCard}>
              <div className={styles.stepInfo}>
                <div className={styles.stepIcon}>
                  <BarChartOutlined />
                </div>
                <div>
                  <div className={styles.stepTitle}>
                    Configure Eligible Rules
                  </div>
                  <p className={styles.stepDescription}>Map eligible fields</p>
                </div>
              </div>
              <Button
                type="primary"
                size="large"
                className={styles.stepButton}
                onClick={handleCreateEligibleRules}
              >
                <PlusOutlined /> Create Eligible Rules
              </Button>
            </div>
          )
        )}
      </div>

      {TEMPLATE_TYPES.map((type, idx) => {
        const list = templatesByType[type]
        const activeForType = activeByType[type]
        if (list.length === 0) {
          return null
        }
        return (
          <Card
            className={styles.historySection}
            style={idx > 0 ? { marginTop: 24 } : undefined}
            key={`history-${type}`}
          >
            <div className={styles.historyHeader}>
              <div className={styles.historyTitleGroup}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  <FileTextOutlined style={{ marginRight: 8 }} />
                  {TEMPLATE_TYPE_LABELS[type]} Field Mapping Template History
                </Typography.Title>
                <p className={styles.historySubtitle}>
                  Track changes and versions of your{' '}
                  {TEMPLATE_TYPE_LABELS[type]} field mapping configurations
                </p>
              </div>
              {activeForType && (
                <Button onClick={() => handleCreateTemplate(type)}>
                  <PlusOutlined /> Create new version
                </Button>
              )}
            </div>
            <Table
              dataSource={list}
              columns={templateColumns}
              rowKey="id"
              loading={isTemplatesPending}
              pagination={false}
              locale={{ emptyText: 'No template history' }}
            />
          </Card>
        )
      })}

      <Card
        className={styles.historySection}
        style={{ marginTop: 24 }}
        key="history-eligible"
      >
        <div className={styles.historyHeader}>
          <div className={styles.historyTitleGroup}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              <BarChartOutlined style={{ marginRight: 8 }} />
              Eligible Rules Mapping Template History
            </Typography.Title>
            <p className={styles.historySubtitle}>
              Track changes and versions of your eligible rules configurations
            </p>
          </div>
          {eligibleRulesHistory.length > 0 && (
            <Button onClick={handleCreateEligibleRules}>
              <PlusOutlined /> Create new version
            </Button>
          )}
        </div>
        <Table
          dataSource={eligibleRulesHistory}
          columns={eligibleRuleColumns as any}
          rowKey="id"
          loading={isEligibleRulesPending}
          pagination={false}
          locale={{ emptyText: 'No eligible rules history' }}
        />
      </Card>

      {showCompanyIgnore ? (
        <Card
          className={styles.historySection}
          style={{ marginTop: 24 }}
          key="company-ignore"
        >
          <div className={styles.historyHeader}>
            <div className={styles.historyTitleGroup}>
              <Typography.Title level={5} style={{ margin: 0 }}>
                <FileTextOutlined style={{ marginRight: 8 }} />
                Ignore Changes
              </Typography.Title>
            </div>
          </div>
          <CompanyIgnore
            key={customerId}
            clientPublicKey={customerId}
            companyName={customerName}
            embedded
          />
        </Card>
      ) : null}
    </Page>
  )
}
